import type { Signal, TemplateRef } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  viewChild,
  inject,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

import type { FullCandidate } from '@core/candidate-types';
import { TranslatePipe } from '@core/i18n-legacy';
import { InterviewResultsSelectors } from '@core/interview-results-data';
import type { InterviewResult } from '@core/interview-results-types';
import { JobsWrapperDataService } from '@core/offers-data';
import type { JobItem } from '@core/offers-types';
import { TalentPoolsDataService } from '@core/talent-pools-data';
import type { TalentPool } from '@core/talent-pools-types';
import { TimeAgoPipe } from '@core/time-legacy';
import {
  DropdownComponent,
  DropdownItemComponent,
  DropdownWidth,
  HeadingComponent,
  IconComponent,
  NestedTemplateTriggerDirective,
} from '@core/user-interface-legacy';
import { groupByKey } from '@core/utils-legacy';

@Component({
  selector: 'rt-edit-quick-evaluation-dropdown',
  template: `
    <rt-dropdown [width]="width()">
      @if (interviewResult()) {
        <rt-dropdown-item [hasHoverEffect]="false" (click)="$event.stopPropagation()">
          <div layout-vertical>
            <rt-heading>{{ 'app.interview.your_last_evaluation' | translate }}</rt-heading>
            <span class="info-text">
              {{
                interviewResult()!.updatedAt
                  | rtTimeAgo: { omitSuffix: false, shortWithSuffix: true }
              }}
              @if (offer()) {
                {{ ' • ' + offer()?.title }}
              }
            </span>
          </div>
        </rt-dropdown-item>
      }
      <rt-dropdown-item
        [targetFor]="addAnotherEvaluationTpl()"
        data-cy="edit-quick-evaluation-dropdown:add-another"
        [hasDivider]="true"
      >
        <div layout layout-align-items-center>
          <rt-icon name="add" size="small" margin-right-small-x />
          {{ 'app.interview.add_another_evaluation' | translate }}
        </div>
      </rt-dropdown-item>
      <rt-dropdown-item (click)="openInEvaluationTab.emit()">
        <div layout layout-align-items-center>
          <rt-icon name="edit" size="small" margin-right-small-x />
          {{ 'app.interview.edit_your_evaluations' | translate }}
        </div>
      </rt-dropdown-item>
    </rt-dropdown>
  `,
  styleUrls: ['./edit-quick-evaluation-dropdown.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DropdownComponent,
    DropdownItemComponent,
    TimeAgoPipe,
    IconComponent,
    TranslatePipe,
    NestedTemplateTriggerDirective,
    HeadingComponent,
  ],
})
export class EditQuickEvaluationDropdownComponent {
  private readonly store = inject(Store);
  private readonly talentPoolsDataService = inject(TalentPoolsDataService);
  private readonly jobsWrapperDataService = inject(JobsWrapperDataService);

  public readonly width = input<DropdownWidth>(DropdownWidth.Large);

  public readonly rtDropdown = viewChild(DropdownComponent);

  public interviewResult = input.required<InterviewResult | null, FullCandidate>({
    alias: 'candidate',
    transform: (candidate: FullCandidate) =>
      this.store.selectSignal(
        InterviewResultsSelectors.currentUserLastCandidateRating(candidate.id),
      )(),
  });

  public offer: Signal<JobItem | TalentPool | null> = computed(() => {
    const jobId = this.interviewResult()?.offerId;
    const talentPoolId = this.interviewResult()?.talentPoolId;

    if (!jobId && !talentPoolId) {
      return null;
    }

    if (!jobId) {
      return this.talentPools()?.[talentPoolId!]?.[0] ?? null;
    }
    return this.jobsWrapperDataService.getJobItemById(jobId!)() ?? null;
  });

  public addAnotherEvaluationTpl = input.required<TemplateRef<any> | undefined>();

  public openInEvaluationTab = output<void>();

  private talentPools = toSignal(
    combineLatest([
      this.talentPoolsDataService.collection$(),
      this.talentPoolsDataService.collection$(true),
    ]).pipe(
      map(([nonArchivedTPs, archivedTPs]) => groupByKey([...nonArchivedTPs, ...archivedTPs], 'id')),
    ),
  );
}
